import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileUp, Upload } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { EdiTag } from "@/components/EdiTag";
import { StatusBadge } from "@/components/StatusBadge";

export const EdiImportDialog = ({ open, onOpenChange, onDone, initialRaw = "", title = "Import inbound EDI" }) => {
  const [raw, setRaw] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (open) {
      setRaw(initialRaw);
      setResults(null);
      setName("");
    }
  }, [open, initialRaw]);

  const loadSample = async (k) => {
    const r = await api.get("/edi/samples");
    setRaw(r.data[k]);
    setName(`${k}_sample.txt`);
  };
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setName(f.name);
    f.text().then(setRaw);
  };
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/edi/import", { raw, filename: name });
      setResults(r.data.results);
      const bad = r.data.results.filter((x) => !["processed"].includes(x.status)).length;
      bad ? toast.warning(`Imported with ${bad} exception(s)`) : toast.success("EDI imported and processed");
      onDone?.(r.data.results);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl border-slate-800 bg-[#1F232C] text-slate-100" data-testid="edi-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{title}</DialogTitle>
          <DialogDescription className="text-slate-400">
            Paste or upload X12 004010 from Group O. Supported: 850 Purchase Order, 945 Warehouse Shipping Advice, 861 Receiving Advice, 856 Advance Ship Notice — routed automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-sm border border-slate-700 px-3 py-1.5 text-xs text-slate-200 transition-colors hover:bg-slate-800" data-testid="edi-import-file-label">
            <FileUp className="h-3.5 w-3.5" /> Upload file
            <input type="file" accept=".txt,.edi,.x12,.dat" className="hidden" onChange={onFile} data-testid="edi-import-file-input" />
          </label>
          <Button variant="ghost" size="sm" onClick={() => loadSample("850")} data-testid="edi-load-sample-850" className="text-slate-300 hover:bg-slate-800 hover:text-white">
            Load sample 850 Purchase Order
          </Button>
          <Button variant="ghost" size="sm" onClick={() => loadSample("945")} data-testid="edi-load-sample-945" className="text-slate-300 hover:bg-slate-800 hover:text-white">
            Load sample 945 Shipping Advice
          </Button>
          {name && <span className="self-center font-mono text-xs text-slate-500">{name}</span>}
        </div>
        <Textarea
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder="ISA*00*...~ or ST*850*0001~ ..."
          className="h-64 resize-none border-slate-800 bg-[#111317] font-mono text-xs text-emerald-200 placeholder:text-slate-600"
          data-testid="edi-import-textarea"
        />
        {results && (
          <div className="space-y-2" data-testid="edi-import-results">
            {results.map((r) => (
              <div key={r.message_id} className="flex flex-wrap items-center gap-3 rounded-sm border border-slate-800 bg-[#252A35] px-3 py-2 text-xs">
                <EdiTag code={r.doc_type} className="font-mono text-amber-300" />
                <StatusBadge status={r.status} />
                <span className="text-slate-300">{r.summary}</span>
                {r.error && <span className="text-rose-300">{r.error}</span>}
                {r.related_type === "order" && r.related_id && (
                  <Link to={`/orders/${r.related_id}`} onClick={() => onOpenChange(false)} className="ml-auto text-sky-300 underline-offset-2 hover:underline" data-testid="edi-import-open-order">
                    Open order
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-end">
          <Button onClick={submit} disabled={!raw.trim() || busy} data-testid="edi-import-submit-btn" className="bg-emerald-500 text-white hover:bg-emerald-600">
            <Upload className="mr-1.5 h-4 w-4" /> {busy ? "Processing…" : "Import & process"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
